import { FC, useId } from 'react';
import { ThemePreference } from '@/context/ThemeContext';
import useTheme from '@/hooks/useTheme';
import useSession from '@/hooks/useSession';
import styles from './theme-picker.module.scss';

const OPTIONS: {
  value: ThemePreference;
  label: string;
  hint?: string;
}[] = [
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
  { value: 'system', label: 'System', hint: 'Follows your device' },
];

/* A miniature of the app: sidebar with logo and New Thread, page with two cards */
const Preview: FC<{ theme: 'light' | 'dark' }> = ({ theme }) => (
  <span className={styles[`preview-${theme}`]}>
    <span className={styles['preview-side']}>
      <i className={styles['preview-logo']} />
      <i className={styles['preview-brand']} />
      <i className={styles['preview-line']} />
      <i className={styles['preview-line']} />
    </span>
    <span className={styles['preview-main']}>
      <i className={styles['preview-card']} />
      <i className={styles['preview-card']} />
      <i className={styles['preview-line']} />
    </span>
  </span>
);

/**
 * A native radio group drawn as preview cards: arrow keys move between options
 * and screen readers announce "Theme, Dark, radio button, 2 of 3". Each preview
 * applies that theme's real tokens, so it always matches what you get.
 */
const ThemePicker: FC = () => {
  const { preference, setPreference } = useTheme();
  const { pushUserEvent } = useSession();
  const name = useId();

  const handleChange = (value: ThemePreference) => {
    setPreference(value);
    pushUserEvent(`set_theme_${value}`);
  };

  return (
    <fieldset className={styles['theme-picker']}>
      <legend className={styles['legend']}>Theme</legend>
      <div className={styles['options']}>
        {OPTIONS.map(({ value, label, hint }) => (
          <label key={value} className={styles['option']}>
            <input
              className={styles['input']}
              type="radio"
              name={name}
              value={value}
              checked={preference === value}
              onChange={() => handleChange(value)}
            />
            <span className={styles['card']}>
              <span className={styles['preview']} aria-hidden="true">
                {value !== 'dark' && <Preview theme="light" />}
                {value !== 'light' && <Preview theme="dark" />}
              </span>
              <span className={styles['label']}>
                <span className={styles['radio']} />
                {label}
                {hint && <span className={styles['hint']}>{hint}</span>}
              </span>
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  );
};

export default ThemePicker;
